<template>
  <b-container fluid class="layout-wide py-5">
    <h2 class="font-weight-bold mb-4">
      <span class="text-primary">대시보드</span> / 실적 및 통계
    </h2>

    <b-card class="border-0 shadow-sm rounded-lg mb-4">
      <b-form @submit.prevent="fetchDashboardData">
        <b-row>
          <b-col lg="6" class="mb-3 mb-lg-0 border-right-lg">
            <h6 class="font-weight-bold text-muted mb-3">📅 기간 설정</h6>
            <div class="d-flex align-items-end flex-wrap">
              <b-form-group label="기준 년도" label-class="small text-muted font-weight-bold" class="mb-2 mr-2" style="min-width: 100px;">
                <b-form-select v-model="selectedYear" :options="yearOptions" @change="updateDatesByYear" class="jinro-input font-weight-bold"></b-form-select>
              </b-form-group>

              <b-form-group label="시작일" label-class="small text-muted font-weight-bold" class="mb-2 mr-2 flex-grow-1" style="min-width: 130px;">
                <b-form-input type="date" v-model="search.from" class="jinro-input"></b-form-input>
              </b-form-group>

              <b-form-group label="종료일" label-class="small text-muted font-weight-bold" class="mb-2 flex-grow-1" style="min-width: 130px;">
                <b-form-input type="date" v-model="search.to" class="jinro-input"></b-form-input>
              </b-form-group>
            </div>
          </b-col>

          <b-col lg="6">
            <h6 class="font-weight-bold text-muted mb-3">🔍 상세 조건</h6>
            <b-row class="align-items-end"> <b-col md="3">
              <b-form-group label="청구 상태" label-class="small text-muted font-weight-bold" class="mb-2">
                <b-form-select v-model="filters.billingStatus" :options="billingOptions" class="jinro-input">
                  <template #first><option :value="null">전체</option></template>
                </b-form-select>
              </b-form-group>
            </b-col>

              <b-col md="3">
                <b-form-group label="지급 상태" label-class="small text-muted font-weight-bold" class="mb-2">
                  <b-form-select v-model="filters.paymentStatus" :options="paymentOptions" class="jinro-input">
                    <template #first><option :value="null">전체</option></template>
                  </b-form-select>
                </b-form-group>
              </b-col>

              <b-col md="6" class="d-flex align-items-center justify-content-end mb-2">
                <b-form-checkbox v-model="filters.excludeProxy" switch size="lg" class="mr-3 pt-1 font-weight-bold text-muted">
                  대리청구 제외
                </b-form-checkbox>

                <b-button type="submit" variant="primary" class="px-4 rounded-pill font-weight-bold shadow-sm" style="height: 45px;">
                  데이터 조회
                </b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <b-row class="mb-4">
      <b-col md="6" class="mb-3 mb-md-0">
        <b-card class="border-0 shadow-sm rounded-lg text-center py-3">
          <div class="small text-muted font-weight-bold mb-2">총 작업 일수</div>
          <h3 class="font-weight-bold text-info">{{ totalPeriodDays }} <span class="small">일</span></h3>
        </b-card>
      </b-col>
      <b-col md="6">
        <b-card class="border-0 shadow-sm rounded-lg text-center py-3">
          <div class="small text-muted font-weight-bold mb-2">총 청구 금액</div>
          <h3 class="font-weight-bold text-success">{{ totalPeriodAmount.toLocaleString() }} <span class="small">원</span></h3>
        </b-card>
      </b-col>
    </b-row>

    <b-card title="월별 작업 및 수익 추이" title-tag="h5" class="border-0 shadow-sm rounded-lg">
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0 font-weight-bold">월별 작업 및 수익 추이</h5>
          <b-badge variant="light" class="text-muted p-2">{{ periodLabel }}</b-badge>
        </div>
      </template>
      <div class="chart-container">
        <canvas id="monthlyChart"></canvas>
      </div>
    </b-card>

    <div class="mt-5 mb-3">
      <h4 class="font-weight-bold mb-3 border-left pl-3" style="border-width: 4px !important; border-color: #17a2b8 !important;">
        수입 지출 내역 분석
      </h4>
    </div>

    <b-card class="border-0 shadow-sm rounded-lg mb-4 bg-light">
      <div class="d-flex align-items-center flex-wrap">
        <div class="mr-5 d-flex align-items-center">
          <span class="small text-muted font-weight-bold mr-2">총 수입</span>
          <strong class="text-primary h4 mb-0">{{ accountTotalIncome.toLocaleString() }}</strong>
          <span class="small text-muted ml-1">원</span>
        </div>

        <div class="mr-5 d-flex align-items-center">
          <span class="small text-muted font-weight-bold mr-2">총 지출</span>
          <strong class="text-danger h4 mb-0">{{ accountTotalExpense.toLocaleString() }}</strong>
          <span class="small text-muted ml-1">원</span>
        </div>

        <div class="d-flex align-items-center border-left pl-4">
          <span class="small text-muted font-weight-bold mr-2">총 합계</span>
          <strong :class="accountTotalBalance >= 0 ? 'text-success' : 'text-danger'" class="h3 mb-0">
            {{ accountTotalBalance.toLocaleString() }}
          </strong>
          <span class="small text-muted ml-1">원</span>
        </div>
      </div>
    </b-card>

    <b-row>
      <b-col lg="12" class="mb-4">
        <b-card title="월별 수입/지출 흐름" class="border-0 shadow-sm rounded-lg h-100">
          <div style="height: 350px; position: relative;">
            <canvas id="accountFlowChart"></canvas>
          </div>
        </b-card>
      </b-col>

      <b-col lg="6" class="mb-4">
        <b-card title="기간 내 수입 분류별 점유율" class="border-0 shadow-sm rounded-lg h-100">
          <div style="height: 300px; position: relative;">
            <canvas id="dashboardIncomePieChart"></canvas>
          </div>
        </b-card>
      </b-col>

      <b-col lg="6" class="mb-4">
        <b-card title="기간 내 지출 분류별 점유율" class="border-0 shadow-sm rounded-lg h-100">
          <div style="height: 300px; position: relative;">
            <canvas id="dashboardExpensePieChart"></canvas>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
/* eslint-disable vue/multi-word-component-names */
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
  name: 'MainDashboard',
  data() {
    const now = new Date();
    // 한 달 전의 2일로 설정하여 안전하게 기간 확보
    const oneYearAgo = new Date(now.getFullYear(), now.getMonth() - 11, 2);
    const currentYear = new Date().getFullYear();

    return {
      selectedYear: currentYear,
      yearOptions: [
        { value: currentYear, text: `${currentYear}년` }
      ],
      search: {
        from: oneYearAgo.toISOString().split('T')[0],
        to: now.toISOString().split('T')[0]
      },
      // [추가] 필터 옵션들
      filters: {
        billingStatus: null, // null = 전체
        paymentStatus: null, // null = 전체
        excludeProxy: true   // 기본값: 체크됨
      },
      billingOptions: ['미완료', '완료', '대리청구완료'],
      paymentOptions: ['미완료', '완료', '지급예정', '지급불가'],
      // [신규] 수입지출 차트용 변수
      accountFlowChart: null,
      dashboardIncomePieChart: null,
      dashboardExpensePieChart: null,
      accountTotalIncome: 0,
      accountTotalExpense: 0,
      stats: [],
      chart: null,
      // 차트 색상 팔레트 (AccountBookPage와 통일)
      chartColors: ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6610f2', '#fd7e14', '#20c997', '#e83e8c', '#6c757d']
    };
  },
  computed: {
    totalPeriodDays() {
      return this.stats.reduce((acc, cur) => acc + (cur.totalWorkDays || 0), 0);
    },
    totalPeriodAmount() {
      return this.stats.reduce((acc, cur) => acc + (cur.totalAmount || 0), 0);
    },
    periodLabel() {
      if (!this.search.from || !this.search.to) return '기간 미설정';
      const startDate = new Date(this.search.from);
      const endDate = new Date(this.search.to);
      const diffMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
          (endDate.getMonth() - startDate.getMonth());
      const totalMonths = diffMonths + 1;
      if (totalMonths <= 0) return '기간 설정 오류';
      return `최근 ${totalMonths}개월`;
    },
    // [신규] 총 합계 (수입 - 지출) 계산
    accountTotalBalance() {
      return this.accountTotalIncome - this.accountTotalExpense;
    }
  },
  async created() {
    await this.fetchAvailableYears();
    this.updateDatesByYear();
  },
  mounted() {
    // created에서 updateDatesByYear 호출 시 자동으로 fetchDashboardData 호출함
  },
  methods: {
    getCategoryColor(categoryName) {
      const colors = [
        '#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8',
        '#6610f2', '#fd7e14', '#20c997', '#e83e8c', '#6c757d',
        '#343a40', '#6f42c1', '#e0a800', '#c82333', '#138496'
      ];

      if (!categoryName) return '#ccc';

      let hash = 0;
      for (let i = 0; i < categoryName.length; i++) {
        hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
      }

      const index = Math.abs(hash % colors.length);
      return colors[index];
    },
    async fetchAvailableYears() {
      try {
        const userId = localStorage.getItem('userId');
        const res = await this.$axios.get('/api/dashboard/years', { params: { userId } });
        const years = res.data.data || [];
        if (years.length > 0) {
          const options = years.map(item => ({ value: item.year, text: `${item.year}년` }));
          const currentYear = new Date().getFullYear();
          const isYearExists = options.some(opt => opt.value === currentYear);
          if (!isYearExists) {
            options.push({ value: currentYear, text: `${currentYear}년` });
          }
          this.yearOptions = options.sort((a, b) => b.value - a.value);
        }
      } catch (err) {
        console.error("년도 목록 로드 실패", err);
      }
    },
    updateDatesByYear() {
      const year = this.selectedYear;
      const today = new Date();
      this.search.from = `${year}-01-01`;

      const endOfYearDate = new Date(year, 11, 31);
      if (today < endOfYearDate) {
        this.search.to = today.toISOString().split('T')[0];
      } else {
        const y = endOfYearDate.getFullYear();
        const m = String(endOfYearDate.getMonth() + 1).padStart(2, '0');
        const d = String(endOfYearDate.getDate()).padStart(2, '0');
        this.search.to = `${y}-${m}-${d}`;
      }
      this.fetchDashboardData();
    },
    async fetchDashboardData() {
      try {
        const userId = localStorage.getItem('userId');
        // [수정] params에 from, to와 함께 filters 객체 내용 전개하여 전달
        const res = await this.$axios.get('/api/dashboard/monthly', {
          params: {
            userId,
            from: this.search.from,
            to: this.search.to,
            // 필터 추가
            billingStatus: this.filters.billingStatus,
            paymentStatus: this.filters.paymentStatus,
            excludeProxy: this.filters.excludeProxy
          }
        });
        this.stats = res.data.data || [];
        this.renderChart();

        // [신규] 2. 수입/지출 내역 데이터 호출 (AccountBook 기준)
        await this.fetchAccountStats(userId);

      } catch (err) {
        console.error("데이터 로드 실패", err);
      }
    },
    // [신규] 수입/지출 통계 조회 및 차트 그리기
    async fetchAccountStats(userId) {
      try {
        const res = await this.$axios.get('/api/account/stats', {
          params: {
            userId: userId,
            from: this.search.from,
            to: this.search.to
          }
        });

        const data = res.data.data;
        if(data) {
          this.renderAccountCharts(data);
        }
      } catch (e) {
        console.error("수입지출 통계 로드 실패", e);
      }
    },

    // [신규] 차트 렌더링 로직
    renderAccountCharts(data) {
      // 1. 월별 흐름 차트 (Bar)
      const flowData = data.monthlyFlow || [];
      this.accountTotalIncome = flowData.reduce((acc, cur) => acc + (cur.totalIncome || 0), 0);
      this.accountTotalExpense = flowData.reduce((acc, cur) => acc + (cur.totalExpense || 0), 0);
      const labels = flowData.map(d => d.period); // '2025-01' 형태
      const incomeData = flowData.map(d => d.totalIncome);
      const expenseData = flowData.map(d => d.totalExpense);

      const ctxFlow = document.getElementById('accountFlowChart');
      if (ctxFlow) {
        if (this.accountFlowChart) this.accountFlowChart.destroy();
        this.accountFlowChart = new Chart(ctxFlow, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [
              { label: '수입', data: incomeData, backgroundColor: '#007bff', borderRadius: 4 },
              { label: '지출', data: expenseData, backgroundColor: '#dc3545', borderRadius: 4 }
            ]
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            plugins: {
              legend: { position: 'top', align: 'end' },
              tooltip: { callbacks: { label: (c) => c.dataset.label + ': ' + parseInt(c.raw).toLocaleString() + '원' } }
            },
            scales: {
              y: { beginAtZero: true, ticks: { callback: (v) => v.toLocaleString() } },
              x: { grid: { display: false } }
            }
          }
        });
      }

      // 2. 카테고리 데이터 가공
      const catData = data.categoryDist || [];
      const incomeCats = {};
      const expenseCats = {};

      catData.forEach(item => {
        if (item.type === '수입') incomeCats[item.category] = item.income;
        if (item.type === '지출') expenseCats[item.category] = item.expense;
      });

      // 3. 수입 파이 차트
      this.drawPieChart('dashboardIncomePieChart', incomeCats, this.dashboardIncomePieChart, (chart) => this.dashboardIncomePieChart = chart);

      // 4. 지출 파이 차트
      this.drawPieChart('dashboardExpensePieChart', expenseCats, this.dashboardExpensePieChart, (chart) => this.dashboardExpensePieChart = chart);
    },

    // [신규] 파이 차트 그리기 헬퍼 함수
    drawPieChart(elementId, dataMap, chartInstance, setChartInstance) {
      const ctx = document.getElementById(elementId);
      if (!ctx) return;
      if (chartInstance) chartInstance.destroy();

      const labels = Object.keys(dataMap);
      const values = Object.values(dataMap);

      // 데이터가 없을 경우 처리
      if (labels.length === 0) {
        // 빈 차트 혹은 메시지 처리 가능 (여기서는 빈 상태로 둠)
        return;
      }

      const newChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: values,
            backgroundColor: labels.map(name => this.getCategoryColor(name)),
            hoverOffset: 4
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right', labels: { boxWidth: 12 } },
            tooltip: {
              callbacks: {
                label: function(c) {
                  const value = c.raw;
                  const total = c.chart._metasets[c.datasetIndex].total;
                  const percentage = ((value / total) * 100).toFixed(1) + '%';
                  return c.label + ': ' + parseInt(value).toLocaleString() + '원 (' + percentage + ')';
                }
              }
            }
          }
        }
      });
      setChartInstance(newChart);
    },
    renderChart() {
      const canvas = document.getElementById('monthlyChart');
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (this.chart) this.chart.destroy();

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.stats.map(s => s.month),
          datasets: [
            {
              // [변경] 작업 일수: 라인 차트 (Cyan 색상)
              type: 'line',
              label: '작업 일수 (일)',
              data: this.stats.map(s => s.totalWorkDays),
              borderColor: '#17a2b8', // 기존 Info 색상 유지
              backgroundColor: '#17a2b8',
              borderWidth: 2,
              tension: 0.1, // 선을 약간 부드럽게
              pointStyle: 'circle',
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: false,
              yAxisID: 'y',
              order: 0 // 라인이 막대보다 앞으로 오도록 설정
            },
            {
              // [변경] 금액: 막대 차트 (Green 색상)
              type: 'bar',
              label: '금액 (원)',
              data: this.stats.map(s => s.totalAmount),
              // [요청 반영] 기존 코드의 투명도(0.6) 적용
              backgroundColor: 'rgba(40, 167, 69, 0.6)',
              borderColor: '#28a745',
              borderWidth: 1,
              borderRadius: 6, // 기존 둥근 모서리 유지
              hoverBackgroundColor: 'rgba(40, 167, 69, 0.8)',
              yAxisID: 'y1',
              order: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              position: 'top', // [요청 반영] 다시 가운데 정렬 (기본값)
              labels: {
                usePointStyle: true,
                boxWidth: 8, // 아이콘 크기 살짝 키움 (가시성 확보)
                padding: 40, // [요청 반영] 색상과 글씨, 항목 사이 여유 공간 추가
                font: {
                  family: "'Noto Sans KR', sans-serif",
                  size: 12,
                  weight: 'bold'
                },
                color: '#6c757d'
              }
            },
            tooltip: {
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              titleColor: '#333',
              bodyColor: '#333',
              borderColor: '#ddd',
              borderWidth: 1,
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label = label.split('(')[0].trim() + ': ';
                  }
                  if (context.parsed.y !== null) {
                    if(context.dataset.yAxisID === 'y1') {
                      label += context.parsed.y.toLocaleString() + ' 원';
                    } else {
                      label += context.parsed.y + ' 일';
                    }
                  }
                  return label;
                }
              }
            }
          },
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: { display: true, text: '일수(일)', color: '#17a2b8', font: { weight: 'bold' } },
              grid: { color: '#f0f0f0' },
              suggestedMin: 0
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              title: { display: true, text: '금액(원)', color: '#28a745', font: { weight: 'bold' } },
              grid: { drawOnChartArea: false },
              ticks: {
                callback: function(value) {
                  return value.toLocaleString();
                }
              },
              beginAtZero: true
            },
            x: {
              grid: { display: false }
            }
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.jinro-input {
  border-radius: 12px;
  height: 45px;
  border: 1px solid #e0e0e0;
}

@media (min-width: 992px) {
  .border-right-lg {
    border-right: 1px solid #e9ecef;
  }
}

.card { transition: transform 0.2s ease-in-out; }
.chart-container { position: relative; height: 400px; width: 100%; }
@media (max-width: 576px) {
  h2 { font-size: 1.5rem; }
  .chart-container { height: 300px; }
}
.text-primary { color: #007bff !important; }
.bg-light { background-color: #f8f9fa !important; }
</style>
